import { useState, useMemo } from 'react'
import Box from '@components/base/Box'
import Text from '@components/base/Text'
import { useTheme } from '@libs/useTheme'
import type {
  Event,
  EventDetails,
  TicketOrder,
  User,
} from '@graphql/__generated/graphql'
import { v4 } from 'uuid'
import { useAuth } from '@libs/Auth/useAuth'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { GET_USERS_BY_IDS } from '@graphql/users/GetUsersByIds'
import { GET_EVENT_BY_ID } from '@graphql/events/GetEventById'
import LoadingSpinner from '@components/base/LoadingSpinner'
import { GET_EVENTS_BY_IDS } from '@graphql/events/GetEventsByIds'
import OrderModal from '@components/order-manager/OrderModal'
import { UPDATE_ORDER } from '@graphql/events/updateOrder'
import {
  MdChevronRight,
  MdOutlineConfirmationNumber,
  MdOutlineSearch,
} from 'react-icons/md'

type PaymentTicketOrder = TicketOrder & {
  paymentProcessor?: 'STRIPE' | 'GOCARDLESS' | null
  paymentStatus?:
    | 'PENDING'
    | 'SUCCEEDED'
    | 'FAILED'
    | 'REFUNDED'
    | 'CHARGED_BACK'
    | null
  paymentLastUpdated?: string | null
}

export type OrderData = PaymentTicketOrder & {
  event?: Partial<Event>
  user?: Partial<User>
}

const paymentStatusStyles: Record<string, string> = {
  SUCCEEDED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  PENDING: 'bg-amber-100 text-amber-900 border-amber-200',
  FAILED: 'bg-red-100 text-red-800 border-red-200',
  REFUNDED: 'bg-slate-200 text-slate-800 border-slate-300',
  CHARGED_BACK: 'bg-red-100 text-red-800 border-red-200',
}

const getFiltered = (
  orders: (PaymentTicketOrder | null | undefined)[],
  filter: string
) => {
  if (!filter || !orders) return orders
  const lowercasedSearchText = filter.toLowerCase()

  const payload = orders.filter((order) => {
    const orderEmail =
      order?.tickets?.flatMap((ticket) =>
        ticket.email ? [ticket.email.toLowerCase()] : []
      ) ?? []
    const orderNames =
      order?.tickets?.reduce(
        (acc, t) => [...acc, t.fullName.toLowerCase()],
        [] as string[]
      ) ?? []

    return (
      orderEmail.find((e) => e.includes(lowercasedSearchText)) ||
      orderNames.find((n) => n.includes(lowercasedSearchText)) ||
      order?.orderId.includes(lowercasedSearchText) ||
      order?.eventId?.includes(lowercasedSearchText) ||
      order?.userId?.includes(lowercasedSearchText) ||
      (order?.paid ? 'paid' : 'pending').includes(lowercasedSearchText) ||
      order?.quantity?.toString().includes(lowercasedSearchText)
    )
  })
  return payload
}

const TableOrders = ({
  orders,
  className,
  refetch,
}: {
  orders: PaymentTicketOrder[]
  refetch: () => void
  className?: string
}) => {
  const { profile } = useAuth()
  const { theme } = useTheme()

  const org = profile?.company ?? profile?.university!

  const [filter, setFilter] = useState<string>('')
  const [users, setUsers] = useState<User[]>([])
  const [eventsData, setEventsData] = useState<Event[]>([])

  const orderEvents = orders
    ?.map((o) => o?.eventId)
    .filter((id) => typeof id === 'string' && id.length === 36)
    .reduce((acc, id) => {
      if (!id) return acc
      if (!acc.includes(id)) return [...acc, id]
      return acc
    }, [] as string[])

  const orderUsers = orders?.map((o) => o?.userId).filter((o) => !!o)

  const { loading: eventsLoading } = useQuery<{
    getEventsByIds: Event[]
  }>(GET_EVENTS_BY_IDS, {
    variables: {
      eventIds: orderEvents,
    },
    skip: orderEvents.length < 1,
    onCompleted: (data) => {
      setEventsData(data.getEventsByIds)
    },
  })

  const { loading: usersLoading } = useQuery<{
    getUsersByIds: { items: User[] }
  }>(GET_USERS_BY_IDS, {
    variables: { input: { userIds: orderUsers as string[] } },
    skip: orderUsers.length < 1,
    onCompleted: (data) => {
      setUsers(data.getUsersByIds.items)
    },
  })

  const filteredData = useMemo(() => {
    return getFiltered(orders, filter).map((o) => {
      const user = users.find((u) => u.userId === o?.userId)
      const event = eventsData.find((e) => e.eventId === o?.eventId)
      const payload = {
        ...o,
        user,
        event,
      } as OrderData
      return payload
    })
  }, [orders, filter, users, eventsData])

  const [isOrderModalOpen, setOrderModalOpen] = useState(false)
  const [orderModalData, setOrderModalData] = useState<OrderData>()

  const [updateOrder] = useMutation(UPDATE_ORDER)

  const handleSubmit = (data: OrderData) => {
    if (data.orderId) {
      const ticketPayload = data.tickets?.map((t) => ({
        email: t.email,
        fullName: t.fullName,
        title: t.title,
        registrationVisible: t.registrationVisible,
      }))

      updateOrder({
        variables: {
          input: {
            orderId: data.orderId,
            paid: data.paid,
            ticketsPurchased: ticketPayload,
          },
        },
      }).then(() => refetch())
    }
  }

  return (
    <>
      <OrderModal
        show={isOrderModalOpen}
        setShow={setOrderModalOpen}
        className="!max-w-5xl md:!min-w-0"
        title={'Order Manager'}
        data={orderModalData}
        onComplete={(data) => handleSubmit(data)}
      />
      <Box className="w-full flex justify-center items-center">
        <Box
          color="foregroundColour"
          className={`w-full max-w-[1500px] rounded-3xl border border-forumm-menu-border p-3 shadow-md sm:p-5 ${className}`}
        >
          <Box className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <Box>
              <Text className="text-xl font-semibold md:text-2xl">
                {`${org}'s orders`}
              </Text>
              <Box className="mt-1 text-sm opacity-70">
                {filteredData.length} of {orders.length}{' '}
                {orders.length === 1 ? 'order' : 'orders'}
              </Box>
            </Box>
            <label className="relative block w-full md:max-w-md">
              <span className="sr-only">Search orders</span>
              <MdOutlineSearch
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-slate-500"
              />
              <input
                type="search"
                value={filter}
                className="h-11 w-full rounded-lg border border-slate-300 bg-white py-2 pl-10 pr-3 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-500 focus:border-forumm-blue focus:ring-2 focus:ring-forumm-blue/20"
                placeholder="Search attendee, event, email or order ID"
                onChange={(event) => setFilter(event.target.value)}
              />
            </label>
          </Box>

          <Box
            className="max-h-[560px] overflow-y-auto rounded-xl border"
            style={{ borderColor: theme.borderColour }}
          >
            <Box
              className="sticky top-0 z-10 hidden grid-cols-[minmax(220px,1.4fr)_minmax(180px,1fr)_minmax(180px,0.9fr)_100px_36px] items-center gap-4 border-b px-4 py-3 text-xs font-semibold uppercase tracking-wide md:grid"
              style={{
                backgroundColor: theme.backgroundColourSecondary,
                borderColor: theme.borderColour,
              }}
            >
              <Box>Event and order</Box>
              <Box>Primary attendee</Box>
              <Box>Payment</Box>
              <Box className="text-center">Tickets</Box>
              <span className="sr-only">Open</span>
            </Box>

            {filteredData.length === 0 ? (
              <Box className="flex flex-col items-center justify-center px-6 py-16 text-center">
                <MdOutlineSearch
                  aria-hidden="true"
                  className="h-8 w-8 opacity-40"
                />
                <Box className="mt-3 font-semibold">No matching orders</Box>
                <Box className="mt-1 text-sm opacity-70">
                  Try a different attendee, event, email, or order ID.
                </Box>
              </Box>
            ) : (
              filteredData.map((orderData) => {
                const status =
                  orderData.paymentStatus ??
                  (orderData.paid ? 'SUCCEEDED' : 'PENDING')
                const processor = orderData.paymentProcessor ?? 'LEGACY'
                const eventTitle = orderData.event?.event?.title ?? 'Event unavailable'
                const attendee = orderData.tickets?.[0]

                return (
                  <button
                    type="button"
                    aria-label={`Manage order for ${eventTitle}`}
                  key={orderData.orderId}
                    className="grid w-full cursor-pointer grid-cols-[minmax(0,1fr)_auto] gap-3 border-b p-4 text-left transition-colors last:border-b-0 hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-forumm-blue md:grid-cols-[minmax(220px,1.4fr)_minmax(180px,1fr)_minmax(180px,0.9fr)_100px_36px] md:items-center md:gap-4"
                    style={{
                      borderColor: theme.borderColour,
                      color: theme.textColour,
                    }}
                  onClick={() => {
                    setOrderModalOpen(true)
                    setOrderModalData(orderData)
                  }}
                >
                    <Box className="min-w-0">
                      <Box className="truncate text-sm font-semibold sm:text-base">
                        {eventsLoading ? <LoadingSpinner size="small" /> : eventTitle}
                      </Box>
                      <Box className="mt-1 truncate font-mono text-xs opacity-60">
                        {orderData.orderId}
                      </Box>
                    </Box>

                    <MdChevronRight
                      aria-hidden="true"
                      className="h-5 w-5 self-center opacity-50 md:order-last"
                    />

                    <Box className="col-span-2 min-w-0 md:col-span-1">
                      <Box className="truncate text-sm font-medium">
                        {attendee?.fullName ?? 'Unnamed attendee'}
                      </Box>
                      <Box className="mt-0.5 truncate text-xs opacity-60">
                        {attendee?.email ?? 'No email'}
                      </Box>
                    </Box>

                    <Box className="col-span-2 flex flex-wrap items-center gap-2 md:col-span-1">
                      <span
                        className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
                          paymentStatusStyles[status] ??
                          paymentStatusStyles.PENDING
                        }`}
                      >
                        {status.replaceAll('_', ' ')}
                      </span>
                      <span className="text-xs font-semibold opacity-60">
                        {processor}
                      </span>
                    </Box>

                    <Box className="col-span-2 flex items-center gap-1.5 text-sm font-medium md:col-span-1 md:justify-center">
                      <MdOutlineConfirmationNumber
                        aria-hidden="true"
                        className="h-4 w-4 opacity-60"
                      />
                      {orderData.quantity ?? orderData.tickets?.length ?? 1}
                    </Box>
                  </button>
                )
              })
            )}
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default TableOrders
